import React from "react";
import { NavLink, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ChevronRight } from "lucide-react";
import Logo from "./Logo";

export default function Sidebar({
  items,
  user,
  roleLabel,
  onLogout,
  collapsed,
}) {
  return (
    <aside
      className={`relative h-screen sticky top-0 z-30 flex flex-col border-r border-slate-200 bg-white/85 backdrop-blur-md transition-[width] duration-300 ${
        collapsed ? "w-[78px]" : "w-[260px]"
      }`}
    >
      {/* Decorative gold strip */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#1E3A8A] via-[#C49A3A] to-[#1E3A8A]" />

      <div className="p-4 border-b border-slate-200/80 flex items-center justify-between gap-2 min-w-0">
        <Link to="/" className="min-w-0 flex-1 overflow-hidden">
          <Logo size={36} showText={!collapsed} />
        </Link>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end}>
            {({ isActive }) => (
              <div
                className={`relative group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "text-cct-blue"
                    : "text-slate-600 hover:text-cct-blue hover:bg-slate-50"
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId={`sidebar-active-${roleLabel}`}
                    className="absolute inset-0 rounded-lg bg-gradient-to-r from-[#1E3A8A]/10 to-[#C49A3A]/8 border border-[#1E3A8A]/20"
                    transition={{ type: "spring", bounce: 0.18, duration: 0.45 }}
                  />
                )}
                {isActive && (
                  <motion.div
                    layoutId={`sidebar-bar-${roleLabel}`}
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-[#C49A3A]"
                    transition={{ type: "spring", bounce: 0.18, duration: 0.45 }}
                  />
                )}
                <it.icon
                  className={`relative w-[18px] h-[18px] shrink-0 ${
                    isActive ? "text-cct-blue" : "text-slate-500 group-hover:text-cct-blue"
                  }`}
                />
                {!collapsed && (
                  <span className="relative flex-1">{it.label}</span>
                )}
                {!collapsed && it.badge !== undefined && it.badge > 0 && (
                  <span
                    className={`relative inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full ${
                      isActive
                        ? "bg-[#C49A3A] text-white"
                        : "bg-slate-200 text-slate-700 group-hover:bg-cct-blue group-hover:text-white"
                    }`}
                  >
                    {it.badge}
                  </span>
                )}
                {!collapsed && isActive && (
                  <ChevronRight className="relative w-4 h-4 text-cct-blue" />
                )}
              </div>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-slate-200/80">
        {!collapsed && user && (
          <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-gradient-to-br from-[#1E3A8A]/5 to-[#C49A3A]/5 mb-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#3B5BC9] text-white flex items-center justify-center font-bold text-sm shadow-sm">
              {(user.name || user.full_name || "A").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-800 truncate">
                {user.name || user.full_name}
              </div>
              <div className="text-[11px] text-slate-500 truncate">
                {roleLabel}
              </div>
            </div>
          </div>
        )}
        <button
          onClick={onLogout}
          className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && "Sign out"}
        </button>
      </div>
    </aside>
  );
}
